import {
  Component,
  getMobileWidth,
  Skeleton,
  theme,
  useSafeCallback,
  useSafeState,
  useUnmountRef
} from '@atomica.co/components';
import { EMPTY, getDayOfWeek, isFuture, Language, Name, toDateStr, URL, Width } from '@atomica.co/utils';
import { FrameId } from '@atomica.co/yosemal';
import { Typography } from '@material-ui/core';
import AddAPhotoOutlinedIcon from '@material-ui/icons/AddAPhotoOutlined';
import { CSSProperties } from '@material-ui/styles';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { FramePos, FRAME_POSITION_FOR_PORTRAIT_POLAROID } from '../../constants/frame-constant';
import PhotoRequest from '../../requests/photo-request';

interface P {
  name: Name;
  photoURL: URL | undefined;
  frameId: FrameId | undefined;
  dueDate: Date;
  onUpload(photoURL: URL): void;
}

const BoardPhotoUploader: React.FC<P> = React.memo(props => {
  const { name, photoURL: initPhotoURL, frameId, dueDate, onUpload } = props;

  const width = useMemo(() => getMobileWidth(), []);
  const frame = useMemo<FramePos | undefined>(
    () => (!!frameId ? FRAME_POSITION_FOR_PORTRAIT_POLAROID[frameId] : undefined),
    [frameId]
  );

  const unmountRef = useUnmountRef();
  const [photoURL, setPhotoURL] = useSafeState<URL | undefined>(unmountRef, initPhotoURL);

  useEffect(() => {
    setPhotoURL(initPhotoURL);
  }, [setPhotoURL, initPhotoURL]);

  const handleBoardPhotoSelected = useSafeCallback(
    async (image): Promise<void> => {
      const uploadedPhotoURL = await PhotoRequest.uploadBoardPhoto(image);
      setPhotoURL(uploadedPhotoURL);
      onUpload(uploadedPhotoURL);
    },
    [setPhotoURL, onUpload]
  );

  return (
    <Component className='board-photo-uploader'>
      <Container width={width}>
        <Card>
          <UploadArea>
            <Uploader
              type='file'
              accept='image/*'
              width={width}
              onChange={e => handleBoardPhotoSelected(e.target.files[0])}
            />

            <PhotoWrapper width={width}>
              {!photoURL && (
                <NoPhoto>
                  <AddAPhotoOutlinedIcon fontSize='large' />

                  <Label>ボード画像を追加しよう</Label>
                </NoPhoto>
              )}

              {!!photoURL && (
                <Skeleton animation='wave' variant='rect' style={styleForProfilePhoto(width)} src={photoURL} />
              )}
            </PhotoWrapper>
          </UploadArea>

          <TitleWrapper>
            <StyledName>{name}</StyledName>

            <DueDate>
              {`${toDateStr(dueDate, Language.JAPANESE)} ${getDayOfWeek(dueDate)} ${
                isFuture(dueDate) ? 'まで' : EMPTY
              }`}
            </DueDate>
          </TitleWrapper>
        </Card>

        {!!frame && <Stamp frame={frame} src={frame.src} />}
      </Container>
    </Component>
  );
});

export default BoardPhotoUploader;

const Container = styled.div<{ width: Width }>`
  width: 100%;
  height: ${props => props.width + theme.mixins.spacing * 6.25}px;
  display: flex;
  align-items: flex-start;
  position: relative;
`;

const Card = styled.div`
  width: 100%;
  height: auto;
  border: 0.25px solid rgba(0, 0, 0, 0.4);
  background: ${theme.mixins.background.white};
  box-shadow: ${theme.mixins.shadow.light};
  display: flex;
  flex-flow: column;
  padding: ${theme.mixins.spacing * 4}px ${theme.mixins.spacing * 2}px ${theme.mixins.spacing}px;
`;

const UploadArea = styled.div`
  width: 100%;
  height: auto;
`;

const Uploader = styled.input<{ width: Width }>`
  width: calc(100% - ${theme.mixins.spacing * 4}px);
  height: ${props => props.width - theme.mixins.spacing * 6}px;
  opacity: 0;
  appearance: none;
  position: absolute;
  z-index: 10;
`;

const PhotoWrapper = styled.div<{ width: Width }>`
  width: 100%;
  height: ${props => props.width - theme.mixins.spacing * 6}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #f3f3f3 0%, #ccc 100%);
  border: ${theme.mixins.border.lightGray};
  box-shadow: inset ${theme.mixins.shadow.light};
`;

const NoPhoto = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
`;

const styleForProfilePhoto = (width: Width): CSSProperties => {
  return {
    width: width - theme.mixins.spacing * 6,
    height: width - theme.mixins.spacing * 6,
    objectFit: 'contain',
    boxShadow: `inset ${theme.mixins.shadow.light}`
  };
};

const Label = styled(Typography)`
  color: ${theme.mixins.typography.fontColor.gray};
  font-size: ${theme.mixins.typography.fontSize.sixteen}px;
  font-weight: ${theme.mixins.typography.fontWeight.sevenHundreds};
  font-family: ${theme.mixins.typography.fontFamily};
  padding-top: ${theme.mixins.spacing}px;
`;

const TitleWrapper = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-flow: column;
  align-items: center;
  padding: ${theme.mixins.spacing / 2}px 0px;
`;

const StyledName = styled(Typography)`
  min-height: 32px;
  color: ${theme.mixins.typography.fontColor.black};
  font-size: ${theme.mixins.typography.fontSize.twenty}px !important;
  font-weight: ${theme.mixins.typography.fontWeight.sevenHundreds} !important;
  font-family: ${theme.mixins.typography.fontFamily} !important;
`;

const DueDate = styled(Typography)`
  min-height: 16px;
  color: ${theme.mixins.typography.fontColor.lightGray};
  font-size: ${theme.mixins.typography.fontSize.twelve}px !important;
  font-weight: ${theme.mixins.typography.fontWeight.fourHundreds} !important;
  font-family: ${theme.mixins.typography.fontFamily} !important;
`;

const Stamp = styled.img<{ frame: FramePos }>`
  width: ${props => props.frame.width};
  margin-top: ${props => props.frame.marginTop};
  margin-left: ${props => props.frame.marginLeft};
  object-fit: contain;
  position: absolute;
  ${theme.mixins.userSelectDisabled};
`;
